import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import { useScheduledProcedureCodeEditDialog } from './hooks';
import { ProcedureInfoSection, DialogActionButtons } from './components';
import type { ScheduledProcedureCodeType } from '../../../../../services/EncounterService/types/encounterTypes';

interface ScheduledProcedureCodeEditDialogProps {
  open: boolean;
  scheduledProcedureCode: ScheduledProcedureCodeType | null;
  onClose: () => void;
  onScheduledProcedureCodeUpdated?: () => void;
}

const ScheduledProcedureCodeEditDialog: React.FC<ScheduledProcedureCodeEditDialogProps> = ({
  open,
  scheduledProcedureCode,
  onClose,
  onScheduledProcedureCodeUpdated
}) => {
  const {
    procedures,
    proceduresLoading,
    isEncounterCancelled,
    handleClose,
    handleSubmit,
    handleDelete,
    formData,
    selectedProcedure,
    error,
    handleProcedureChange,
    handleInputChange,
  } = useScheduledProcedureCodeEditDialog({ 
    open, 
    scheduledProcedureCode, 
    onClose, 
    onScheduledProcedureCodeUpdated 
  });

  if (!scheduledProcedureCode) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#ffffff',
        borderBottom: '2px solid #3b82f6',
        p: 2.5,
        position: 'relative'
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Edit sx={{ color: '#3b82f6', fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            {isEncounterCancelled ? 'View' : 'Edit'} Scheduled Procedure Code
          </Typography>
        </Stack>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9'
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
        <Stack spacing={3}>
          <ProcedureInfoSection
            procedures={procedures}
            proceduresLoading={proceduresLoading}
            selectedProcedure={selectedProcedure}
            code={formData.code}
            description={formData.description}
            isEncounterCancelled={isEncounterCancelled}
            onProcedureChange={handleProcedureChange}
            onDescriptionChange={(value) => handleInputChange('description', value)}
          />

          {/* Error Message Section */}
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <DialogActionButtons
          isEncounterCancelled={isEncounterCancelled}
          onDelete={handleDelete}
          onCancel={handleClose}
          onSubmit={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ScheduledProcedureCodeEditDialog;
